import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import BottomNav from '../components/BottomNav';

interface Promotion {
  id: string;
  name: string;
  target: 'category' | 'product' | 'order';
  target_id: string | null;
  discount_pct: number;
  start_time: string | null;
  end_time: string | null;
  days_of_week: number[] | null;
  start_date: string | null;
  end_date: string | null;
  active: boolean;
}

const DAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

interface FormState {
  name: string;
  target: 'category' | 'product' | 'order';
  target_id: string;
  discount_pct: string;
  start_time: string;
  end_time: string;
  days_of_week: number[];
  start_date: string;
  end_date: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  target: 'category',
  target_id: '',
  discount_pct: '10',
  start_time: '',
  end_time: '',
  days_of_week: [],
  start_date: '',
  end_date: '',
};

export default function PromotionsPage() {
  const { session } = useAuth();
  const [items, setItems] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!session?.storeId) return;
    setLoading(true);
    const { data } = await supabase
      .from('promotions')
      .select('*')
      .eq('store_id', session.storeId)
      .order('created_at', { ascending: false });
    setItems((data as Promotion[] | null) ?? []);
    setLoading(false);
  }, [session?.storeId]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const toggleDay = (d: number) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort(),
    }));
  };

  const handleSave = async () => {
    if (!session?.storeId || !form.name.trim()) return;
    setSaving(true);
    try {
      await supabase.from('promotions').insert({
        store_id: session.storeId,
        name: form.name.trim(),
        target: form.target,
        target_id: form.target_id || null,
        discount_pct: Number(form.discount_pct) || 0,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        days_of_week: form.days_of_week.length > 0 ? form.days_of_week : null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        active: true,
      });
      setForm(EMPTY_FORM);
      setOpenForm(false);
      await fetchItems();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (p: Promotion) => {
    await supabase.from('promotions').update({ active: !p.active }).eq('id', p.id);
    await fetchItems();
  };

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
          Promotions
        </p>
        <button
          type="button"
          onClick={() => setOpenForm(true)}
          className="min-h-[36px] px-3 text-[11px] font-semibold uppercase tracking-wider text-on-primary bg-primary"
        >
          + Ajouter
        </button>
      </header>

      {loading ? (
        <div className="p-6 text-center text-sm text-on-surface-variant">…</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <span className="material-symbols-outlined text-[48px] text-on-surface-variant">
            sell
          </span>
          <p className="text-sm text-on-surface-variant">Aucune promotion</p>
        </div>
      ) : (
        <div className="p-3 space-y-3">
          {items.map((p) => (
            <div
              key={p.id}
              className="bg-surface p-4"
              style={{ border: '0.5px solid #000' }}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-on-surface-strong">{p.name}</p>
                <button
                  type="button"
                  onClick={() => void handleToggleActive(p)}
                  className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 ${
                    p.active ? 'bg-primary text-on-primary' : 'bg-surface text-on-surface-variant'
                  }`}
                  style={{ border: '0.5px solid #000' }}
                >
                  {p.active ? 'ACTIVE' : 'INACTIVE'}
                </button>
              </div>
              <p className="text-xs text-on-surface-variant">
                Réduction <span className="tabular-nums">{p.discount_pct}%</span> · cible{' '}
                {p.target}
              </p>
              {(p.start_time || p.end_time) && (
                <p className="text-[11px] text-on-surface-variant tabular-nums">
                  Plage horaire {p.start_time ?? '—'} → {p.end_time ?? '—'}
                </p>
              )}
              {p.days_of_week && p.days_of_week.length > 0 && (
                <p className="text-[11px] text-on-surface-variant">
                  Jours: {p.days_of_week.map((d) => DAYS[d]).join(' · ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {openForm && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/40"
          onClick={() => setOpenForm(false)}
        >
          <div className="flex-1" />
          <div
            className="bg-surface max-h-[90vh] overflow-y-auto"
            style={{ borderTop: '0.5px solid #000' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-4 h-12"
              style={{ borderBottom: '0.5px solid #e5e7eb' }}
            >
              <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
                Nouvelle promotion
              </p>
              <button
                type="button"
                onClick={() => setOpenForm(false)}
                className="min-h-[40px] min-w-[40px] flex items-center justify-center text-on-surface-variant"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            <div className="p-4 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                  Nom
                </span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full mt-1 min-h-[44px] px-3 text-sm bg-surface"
                  style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                />
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                  Cible
                </span>
                <select
                  value={form.target}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      target: e.target.value as FormState['target'],
                    }))
                  }
                  className="w-full mt-1 min-h-[44px] px-3 text-sm bg-surface"
                  style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                >
                  <option value="category">Catégorie</option>
                  <option value="product">Produit</option>
                  <option value="order">Toute la commande</option>
                </select>
              </label>

              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                  Réduction (%)
                </span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form.discount_pct}
                  onChange={(e) => setForm((f) => ({ ...f, discount_pct: e.target.value }))}
                  className="w-full mt-1 min-h-[44px] px-3 text-sm bg-surface tabular-nums"
                  style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                    Début
                  </span>
                  <input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                    className="w-full mt-1 min-h-[44px] px-3 text-sm bg-surface"
                    style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                    Fin
                  </span>
                  <input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                    className="w-full mt-1 min-h-[44px] px-3 text-sm bg-surface"
                    style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                  />
                </label>
              </div>

              <div>
                <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                  Jours
                </span>
                <div className="mt-1 grid grid-cols-7 gap-2">
                  {DAYS.map((d, idx) => {
                    const active = form.days_of_week.includes(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleDay(idx)}
                        className={`min-h-[40px] text-[12px] font-semibold ${
                          active ? 'bg-primary text-on-primary' : 'bg-surface text-on-surface'
                        }`}
                        style={{ border: '0.5px solid #000' }}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                    Du
                  </span>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                    className="w-full mt-1 min-h-[44px] px-3 text-sm bg-surface tabular-nums"
                    style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-on-surface-variant">
                    Au
                  </span>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                    className="w-full mt-1 min-h-[44px] px-3 text-sm bg-surface tabular-nums"
                    style={{ border: '0.5px solid #e5e7eb', outline: 'none' }}
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving || !form.name.trim()}
                className="w-full min-h-[52px] text-sm font-semibold uppercase tracking-wider text-on-primary bg-primary disabled:opacity-50"
              >
                {saving ? '…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
